import {
  type ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  type CountryCode,
  type CurrencyCode,
} from '@data/shopify/storefront/types'
import { CartContextProvider } from './cart/context'
import { DiscountContextProvider } from './discount/context'
import { type Locale } from './language'
import {
  getShopifyCartIdStorageKey,
  getShopifyShopIdStorageKey,
} from './local-storage'
import { type ShopInformation, getShopInformation } from './shop'
import {
  type ShopifyClient,
  getShopifyStorefrontGraphQlClient,
} from './shopify/graphql/client'
import { getShopifyStoreDomainFromLocales } from './shopify/helpers'

interface ShopContextProps {
  taxRate: number
  taxesIncluded?: boolean
  shopId?: string
  currency?: string
  currencyCode?: CurrencyCode
  countryCode?: CountryCode
  shopifyStoreDomain?: string
  shopifyPrimaryDomain?: string
  shopifyStorefrontGraphQlClient?: ShopifyClient
  loadShop: () => Promise<void>
}

interface ShopContextProviderProps {
  locale: Locale
  site: SanitySiteFragment
  children: ReactNode
}

export const ShopContext = createContext<ShopContextProps>({
  taxRate: 0,
  loadShop: async () => {},
})

export const ShopContextProvider = ({
  locale,
  site,
  children,
}: ShopContextProviderProps) => {
  const [taxesIncluded, setTaxesIncluded] = useState<boolean>()
  const [shopId, setShopId] = useState<string>()
  const [currency, setCurrency] = useState<string>()
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>()
  const [countryCode, setCountryCode] = useState<CountryCode>()
  const [shopifyPrimaryDomain, setShopifyPrimaryDomain] = useState<
    string | undefined
  >(() => getShopifyStoreDomainFromLocales(locale))
  const [shopInformation, setShopInformation] = useState<ShopInformation>({
    locale,
  })

  const localeInitialisedRef = useRef<Locale>()

  const shopifyStorefrontGraphQlClient = useMemo(() => {
    try {
      return getShopifyStorefrontGraphQlClient(locale)
    } catch {
      return
    }
  }, [locale])

  const shopifyStoreDomain = useMemo(
    () => getShopifyStoreDomainFromLocales(locale),
    [locale]
  )

  const taxRate = useMemo(() => site.cartSettings.taxRate ?? 0, [site])

  const loadShop = useCallback(async () => {
    // Clear shop and cart
    setShopInformation({
      locale,
    })

    if (typeof window === 'undefined' || !shopifyStorefrontGraphQlClient) {
      console.warn('Cannot load Shopify shop and cart details')
      return
    }

    localeInitialisedRef.current = locale

    const shopifyCartIdStorageKey = getShopifyCartIdStorageKey(locale)
    const cartId = localStorage.getItem(shopifyCartIdStorageKey) ?? undefined
    const newShopInformation = await getShopInformation(locale, cartId)

    setShopInformation(newShopInformation)
  }, [locale, shopifyStorefrontGraphQlClient])

  // Get Shopify shop and cart information (update when switching language)
  useEffect(() => {
    if (localeInitialisedRef.current === locale) {
      return
    }

    loadShop()
  }, [loadShop, locale])

  useEffect(() => {
    // Save shop ID to local storage
    const shopifyShopIdStorageKey = getShopifyShopIdStorageKey(locale)
    const shopifyShopId = shopInformation.shop?.id

    if (shopifyShopId) {
      localStorage.setItem(shopifyShopIdStorageKey, shopifyShopId)
    }

    if (!shopifyShopId) {
      localStorage.removeItem(shopifyShopIdStorageKey)
    }

    if (!shopInformation.shop) {
      return
    }

    setShopId(shopInformation.shop.id)
    setTaxesIncluded(shopInformation.shop.taxesIncluded)
    setCurrency(
      // Remove HTML tags and value placeholder "{{amount}}" from currency format string
      shopInformation.shop?.moneyFormat
        ?.replace(/<\/?[^>]+(>|$)/g, '')
        ?.replace(/\s*[{]+.*?[}]+\s*/g, '')
    )
    setCurrencyCode(shopInformation.shop?.paymentSettings?.currencyCode)
    setCountryCode(shopInformation.shop?.paymentSettings?.countryCode)
    setShopifyPrimaryDomain(
      shopInformation.shop?.primaryDomain?.host ?? shopifyStoreDomain
    )
  }, [locale, shopifyStoreDomain, shopInformation])

  return (
    <ShopContext.Provider
      value={{
        taxRate,
        taxesIncluded,
        shopId,
        currency,
        currencyCode,
        countryCode,
        shopifyStoreDomain,
        shopifyPrimaryDomain,
        shopifyStorefrontGraphQlClient,
        loadShop,
      }}
    >
      <CartContextProvider
        site={site}
        locale={locale}
        shopInformation={shopInformation}
      >
        <DiscountContextProvider site={site}>
          {children}
        </DiscountContextProvider>
      </CartContextProvider>
    </ShopContext.Provider>
  )
}
